/**
 * The only place the app talks to the API.
 *
 * Session auth is a cookie the browser sends on its own, so nothing here
 * stores a token — there is deliberately no localStorage token to steal
 * (PLAN §17.2). What the client does have to do is echo the CSRF cookie in a
 * header on every mutation.
 */

const CSRF_COOKIE = 'sonologue_csrf'
const CSRF_HEADER = 'X-CSRF-Token'
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

export class ApiError extends Error {
  constructor(problem, status) {
    // No problem body at all is not something the API produces — it always
    // answers `application/problem+json`. It is what a proxy or a gateway in
    // front of the API produces, and a status number is not an answer to the
    // person reading it. The number is still on `status` and `code` for the
    // code that branches on it.
    super(
      problem?.detail ||
        problem?.title ||
        'Sonologue didn’t answer. This is usually temporary — try again.',
    )
    this.name = 'ApiError'
    this.status = status
    this.code = problem?.code || `http-${status}`
    this.problem = problem || {}
    /** Field-level messages from a 422, keyed by field name. */
    this.fieldErrors = Object.fromEntries(
      (problem?.errors || [])
        .filter((e) => Array.isArray(e.loc) && e.loc.length > 1)
        .map((e) => [e.loc[e.loc.length - 1], e.msg]),
    )
  }
}

/**
 * "The record is not there", as opposed to "the request did not work". The
 * catalog pages branch on it three times each — the heading, the retry button
 * and the document title — and all three have to agree.
 */
export function is404(error) {
  return error instanceof ApiError && error.status === 404
}

export function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

async function parseBody(response) {
  if (response.status === 204) return null
  const type = response.headers.get('content-type') || ''
  if (!type.includes('json')) return null
  try {
    return await response.json()
  } catch {
    return null
  }
}

async function send(path, { method = 'GET', body, signal, keepalive } = {}) {
  // A file upload is the one body here that is not JSON, and it is the one
  // that must not have `Content-Type` set: a multipart body carries its
  // boundary in that header, and only the browser knows the boundary it just
  // generated. Setting it by hand produces a request the server cannot parse.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData
  const headers = { Accept: 'application/json' }
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json'
  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE)
    if (token) headers[CSRF_HEADER] = token
  }
  const response = await fetch(path, {
    method,
    headers,
    signal,
    // For a request sent as the page goes away, which the browser would
    // otherwise drop with the page (the import page's cancel on `pagehide`).
    keepalive,
    // Same-origin because the dev server proxies /api. Never 'include' — that
    // would invite a cross-site deployment the SameSite cookie cannot support.
    credentials: 'same-origin',
    body: body === undefined || isForm ? body : JSON.stringify(body),
  })
  return { response, payload: await parseBody(response) }
}

export async function request(path, options = {}) {
  let { response, payload } = await send(path, options)

  // A first mutation in a fresh browser can land before the CSRF cookie has
  // been issued. One GET issues it; retry once, then treat it as real.
  if (response.status === 403 && payload?.code === 'csrf-failed' && !options._retried) {
    await send('/api/v1/auth/session')
    ;({ response, payload } = await send(path, { ...options, _retried: true }))
  }

  if (!response.ok) throw new ApiError(payload, response.status)
  return payload
}

export const api = {
  session: () => request('/api/v1/auth/session'),
  signup: (body) => request('/api/v1/auth/signup', { method: 'POST', body }),
  login: (body) => request('/api/v1/auth/login', { method: 'POST', body }),
  logout: () => request('/api/v1/auth/logout', { method: 'POST' }),
  me: () => request('/api/v1/me'),
  updateMe: (body) => request('/api/v1/me', { method: 'PATCH', body }),
  setAvatar: (file) => {
    const form = new FormData()
    form.append('file', file)
    return request('/api/v1/me/avatar', { method: 'POST', body: form })
  },
  removeAvatar: () => request('/api/v1/me/avatar', { method: 'DELETE' }),
  changePassword: (body) => request('/api/v1/auth/password', { method: 'POST', body }),
  requestPasswordReset: (body) =>
    request('/api/v1/auth/password-reset/request', { method: 'POST', body }),
  confirmPasswordReset: (body) =>
    request('/api/v1/auth/password-reset/confirm', { method: 'POST', body }),
  requestEmailVerification: () =>
    request('/api/v1/auth/email-verification/request', { method: 'POST' }),
  confirmEmailVerification: (body) =>
    request('/api/v1/auth/email-verification/confirm', { method: 'POST', body }),
  usernameAvailable: (username) =>
    request(`/api/v1/users/username-available?username=${encodeURIComponent(username)}`),
  invites: () => request('/api/v1/invites'),
  createInvite: (body) => request('/api/v1/invites', { method: 'POST', body }),
  version: () => request('/api/v1/version'),

  // --- Session audit (1B-4a) -----------------------------------------------
  // A session is named by its handle, which the server derives rather than
  // stores; it is opaque here and is only ever echoed back.
  sessions: () => request('/api/v1/me/sessions'),
  revokeSession: (handle) =>
    request(`/api/v1/me/sessions/${encodeURIComponent(handle)}`, { method: 'DELETE' }),
  revokeOtherSessions: () => request('/api/v1/me/sessions/revoke-others', { method: 'POST' }),

  // --- Two-factor (1B-4a) --------------------------------------------------
  // The challenge from a `totp-required` refusal is passed straight back in
  // and never stored: it lives in one component's state for one screen, the
  // same reason there is no bearer token in `localStorage`.
  twoFactor: () => request('/api/v1/me/two-factor'),
  startTwoFactor: (body) => request('/api/v1/me/two-factor/enrolment', { method: 'POST', body }),
  confirmTwoFactor: (body) =>
    request('/api/v1/me/two-factor/enrolment/confirm', { method: 'POST', body }),
  disableTwoFactor: (body) => request('/api/v1/me/two-factor/disable', { method: 'POST', body }),
  loginWithCode: (body) => request('/api/v1/auth/login/totp', { method: 'POST', body }),
  loginWithRecoveryCode: (body) => request('/api/v1/auth/login/recovery', { method: 'POST', body }),

  // --- Catalog (CAT-001) ---------------------------------------------------
  // `search` is local and instant; `searchRemote` is the explicit "not
  // finding it?" action that spends MusicBrainz's request budget, and the two
  // are separate calls here because they are separate decisions in the UI.
  search: (q, { type = 'all', signal } = {}) =>
    request(`/api/v1/search?q=${encodeURIComponent(q)}&type=${type}`, { signal }),
  searchRemote: (q, { type = 'albums', signal } = {}) =>
    request(`/api/v1/search/remote?q=${encodeURIComponent(q)}&type=${type}`, { signal }),
  materialize: (body) => request('/api/v1/catalog/materialize', { method: 'POST', body }),
  album: (publicId, { signal } = {}) =>
    request(`/api/v1/albums/${encodeURIComponent(publicId)}`, { signal }),
  artist: (publicId, { signal } = {}) =>
    request(`/api/v1/artists/${encodeURIComponent(publicId)}`, { signal }),
  song: (publicId, { signal } = {}) =>
    request(`/api/v1/songs/${encodeURIComponent(publicId)}`, { signal }),

  // --- Ratings (RATE-001) --------------------------------------------------
  // A separate call from `album`: the catalog half of the page is the same for
  // everybody and the rating half is not, and catalog does not depend on
  // ratings (the layer contract in `api/pyproject.toml`). Every write here
  // returns the whole block, so one round trip updates the score, the
  // coverage line and the community histogram together.
  albumRating: (publicId, { signal } = {}) =>
    request(`/api/v1/albums/${encodeURIComponent(publicId)}/rating`, { signal }),
  rateAlbum: (publicId, body) =>
    request(`/api/v1/albums/${encodeURIComponent(publicId)}/rating`, { method: 'PUT', body }),
  unrateAlbum: (publicId) =>
    request(`/api/v1/albums/${encodeURIComponent(publicId)}/rating`, { method: 'DELETE' }),
  rateTrack: (albumId, songId, body) =>
    request(
      `/api/v1/albums/${encodeURIComponent(albumId)}/tracks/${encodeURIComponent(songId)}/rating`,
      { method: 'PUT', body },
    ),
  unrateTrack: (albumId, songId) =>
    request(
      `/api/v1/albums/${encodeURIComponent(albumId)}/tracks/${encodeURIComponent(songId)}/rating`,
      { method: 'DELETE' },
    ),
  songRating: (publicId, { signal } = {}) =>
    request(`/api/v1/songs/${encodeURIComponent(publicId)}/rating`, { signal }),
  rateSong: (publicId, body) =>
    request(`/api/v1/songs/${encodeURIComponent(publicId)}/rating`, { method: 'PUT', body }),
  unrateSong: (publicId) =>
    request(`/api/v1/songs/${encodeURIComponent(publicId)}/rating`, { method: 'DELETE' }),
  myRatings: () => request('/api/v1/me/ratings'),
  // The sub-score vocabulary (1B-4b Phase 1). Fetched rather than hardcoded:
  // it moves with `SUBSCORE_SCHEMA_VERSION`, and a client holding a stale copy
  // would offer a dimension the API refuses. `score.js` duplicates the score
  // *arithmetic* deliberately; a list has no such excuse.
  ratingDimensions: ({ signal } = {}) => request('/api/v1/rating-dimensions', { signal }),
  // And the curated tag vocabulary, which arrives grouped by family because
  // every client draws it grouped (DEC-2026-08-04-041). Rows rather than a
  // constant on the server, so this one genuinely changes between deploys.
  ratingTags: ({ signal } = {}) => request('/api/v1/rating-tags', { signal }),
  // Your own badges (1B-4b Phase 2b). Its own request rather than a field on
  // `/me`, which every page loads — this is read by one section of one screen.
  achievements: ({ signal } = {}) => request('/api/v1/me/achievements', { signal }),
  // Asking the owner for a word the vocabulary does not have (1B-4b Phase 2b).
  // Hung off the record the sheet was open on, which is context the owner
  // reading the queue wants. There is deliberately no call that reads the
  // queue back — a suggestion is visible to nobody but them.
  suggestAlbumTag: (id, text) =>
    request(`/api/v1/albums/${encodeURIComponent(id)}/rating-tag-suggestions`, {
      method: 'POST',
      body: { text },
    }),
  suggestSongTag: (id, text) =>
    request(`/api/v1/songs/${encodeURIComponent(id)}/rating-tag-suggestions`, {
      method: 'POST',
      body: { text },
    }),
  // Blind mode (1B-4b Phase 3). The account-wide preference, and the
  // per-release override — which answers with the whole rating block, because
  // flipping it is exactly what changes the block's contents.
  blindMode: ({ signal } = {}) => request('/api/v1/me/blind-mode', { signal }),
  setBlindMode: (enabled) =>
    request('/api/v1/me/blind-mode', { method: 'PUT', body: { enabled } }),
  setAlbumBlind: (id, blind) =>
    request(`/api/v1/albums/${id}/blind`, { method: 'PUT', body: { blind } }),
  setSongBlind: (id, blind) =>
    request(`/api/v1/songs/${id}/blind`, { method: 'PUT', body: { blind } }),
  // The 1B-2 timeline: own revisions, newest first, cursored on the last
  // row's id. And the artist graph's one batch read of the viewer's scores.
  albumRatingRevisions: (publicId, { beforeId, signal } = {}) =>
    request(
      `/api/v1/albums/${encodeURIComponent(publicId)}/rating/revisions` +
        (beforeId ? `?before_id=${beforeId}` : ''),
      { signal },
    ),
  songRatingRevisions: (publicId, { beforeId, signal } = {}) =>
    request(
      `/api/v1/songs/${encodeURIComponent(publicId)}/rating/revisions` +
        (beforeId ? `?before_id=${beforeId}` : ''),
      { signal },
    ),
  artistRatings: (publicId, { signal } = {}) =>
    request(`/api/v1/artists/${encodeURIComponent(publicId)}/ratings`, { signal }),

  // --- The graph (SOC-001) -------------------------------------------------
  // Follow, block and mute are separate verbs because they are separate
  // promises. A block is symmetric and visible in its effects; a mute is
  // one-directional and the other person never learns of it.
  profile: (handle, { signal } = {}) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}`, { signal }),
  searchUsers: (q, { signal } = {}) =>
    request(`/api/v1/users/search?q=${encodeURIComponent(q)}`, { signal }),
  follow: (handle) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/follow`, { method: 'POST' }),
  unfollow: (handle) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/follow`, { method: 'DELETE' }),
  respondToFollow: (handle, accept) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/follow/respond`, {
      method: 'POST',
      body: { accept },
    }),
  block: (handle) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/block`, { method: 'POST' }),
  unblock: (handle) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/block`, { method: 'DELETE' }),
  mute: (handle) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/mute`, { method: 'POST' }),
  unmute: (handle) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/mute`, { method: 'DELETE' }),
  safety: () => request('/api/v1/me/safety'),
  setEventMute: (body) => request('/api/v1/me/event-mutes', { method: 'PUT', body }),

  // --- Reviews, comments, reactions (REV-001) ------------------------------
  // A review's body crosses the wire twice: `body_md` is what the author
  // typed and edits, `body_html` is what the server made of it. The client
  // never parses Markdown, which is why there is no renderer in this bundle.
  albumReviews: (publicId, { signal } = {}) =>
    request(`/api/v1/albums/${encodeURIComponent(publicId)}/reviews`, { signal }),
  songReviews: (publicId, { signal } = {}) =>
    request(`/api/v1/songs/${encodeURIComponent(publicId)}/reviews`, { signal }),
  saveAlbumReview: (publicId, body) =>
    request(`/api/v1/albums/${encodeURIComponent(publicId)}/review`, { method: 'PUT', body }),
  saveSongReview: (publicId, body) =>
    request(`/api/v1/songs/${encodeURIComponent(publicId)}/review`, { method: 'PUT', body }),
  review: (publicId, { signal } = {}) =>
    request(`/api/v1/reviews/${encodeURIComponent(publicId)}`, { signal }),
  deleteReview: (publicId) =>
    request(`/api/v1/reviews/${encodeURIComponent(publicId)}`, { method: 'DELETE' }),
  comments: (kind, publicId, { signal } = {}) =>
    request(`/api/v1/${kind}/${encodeURIComponent(publicId)}/comments`, { signal }),
  addComment: (kind, publicId, body) =>
    request(`/api/v1/${kind}/${encodeURIComponent(publicId)}/comments`, { method: 'POST', body }),
  deleteComment: (publicId) =>
    request(`/api/v1/comments/${encodeURIComponent(publicId)}`, { method: 'DELETE' }),
  react: (targetType, publicId) =>
    request(`/api/v1/reactions/${targetType}/${encodeURIComponent(publicId)}`, { method: 'POST' }),

  // --- Lists (LIST-001) ----------------------------------------------------
  list: (publicId, { signal } = {}) =>
    request(`/api/v1/lists/${encodeURIComponent(publicId)}`, { signal }),
  createList: (body) => request('/api/v1/lists', { method: 'POST', body }),
  updateList: (publicId, body) =>
    request(`/api/v1/lists/${encodeURIComponent(publicId)}`, { method: 'PATCH', body }),
  deleteList: (publicId) =>
    request(`/api/v1/lists/${encodeURIComponent(publicId)}`, { method: 'DELETE' }),
  addListItem: (publicId, body) =>
    request(`/api/v1/lists/${encodeURIComponent(publicId)}/items`, { method: 'POST', body }),
  removeListItem: (publicId, itemId) =>
    request(`/api/v1/lists/${encodeURIComponent(publicId)}/items/${itemId}`, { method: 'DELETE' }),
  reorderList: (publicId, itemIds) =>
    request(`/api/v1/lists/${encodeURIComponent(publicId)}/reorder`, {
      method: 'POST',
      body: { item_ids: itemIds },
    }),
  // Your own lists, each with `contains`: whether it already holds this one
  // album, song or artist (Q19). What *Add to a list* reads when it opens.
  myLists: (targetType, targetPublicId, { signal } = {}) =>
    request(
      `/api/v1/me/lists?target_type=${encodeURIComponent(targetType)}` +
        `&target_public_id=${encodeURIComponent(targetPublicId)}`,
      { signal },
    ),

  // --- Feed, profile tabs, notifications, charts ---------------------------
  feed: ({ beforeId, signal } = {}) =>
    request(`/api/v1/feed${beforeId ? `?before_id=${beforeId}` : ''}`, { signal }),
  profileReviews: (handle, { signal } = {}) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/reviews`, { signal }),
  profileLists: (handle, { signal } = {}) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/lists`, { signal }),
  profileRatings: (handle, { signal } = {}) =>
    request(`/api/v1/users/${encodeURIComponent(handle)}/ratings`, { signal }),
  myReviews: ({ signal } = {}) => request('/api/v1/me/reviews', { signal }),
  notifications: ({ signal } = {}) => request('/api/v1/notifications', { signal }),
  markNotificationsRead: (ids) =>
    request('/api/v1/notifications/read', { method: 'POST', body: { ids: ids || null } }),
  charts: ({ signal } = {}) => request('/api/v1/charts', { signal }),
  compare: (handle, { signal } = {}) =>
    request(`/api/v1/compare/${encodeURIComponent(handle)}`, { signal }),

  // --- Reports and the admin queue (MOD-001) -------------------------------
  report: (body) => request('/api/v1/reports', { method: 'POST', body }),
  adminReports: (status = 'open', { signal } = {}) =>
    request(`/api/v1/admin/reports?status=${status}`, { signal }),
  resolveReport: (publicId, body) =>
    request(`/api/v1/admin/reports/${encodeURIComponent(publicId)}/resolve`, {
      method: 'POST',
      body,
    }),
  restoreContent: (body) =>
    request('/api/v1/admin/content/restore', { method: 'POST', body }),
  adminOps: ({ signal } = {}) => request('/api/v1/admin/ops', { signal }),
  recomputeCharts: () => request('/api/v1/admin/charts/recompute', { method: 'POST' }),

  // --- Export and deletion (PRIV-001) --------------------------------------
  // The download is a plain link rather than a fetch: the browser's own file
  // save is what a person expects from "download my data", and a blob URL
  // built here would be a second implementation of it.
  privacy: ({ signal } = {}) => request('/api/v1/me/privacy', { signal }),
  requestExport: () => request('/api/v1/me/exports', { method: 'POST' }),
  // Is this link good? The one fetch the export page makes — read-only, and
  // never the archive, so opening the email costs a row lookup and nothing
  // else. A 404 is the server's definitive no; anything else means it did not
  // get to look.
  exportPreflight: (publicId, token) =>
    request(
      `/api/v1/exports/${encodeURIComponent(publicId)}/preflight` +
        (token ? `?token=${encodeURIComponent(token)}` : ''),
    ),
  exportDownloadUrl: (publicId, token) =>
    `/api/v1/exports/${encodeURIComponent(publicId)}/download` +
    (token ? `?token=${encodeURIComponent(token)}` : ''),
  requestDeletion: (body) => request('/api/v1/me/deletion', { method: 'POST', body }),
  cancelDeletion: (token) =>
    request('/api/v1/account/deletion/cancel', { method: 'POST', body: { token } }),
  // Does the cancel link still work? Spends nothing (Q10).
  preflightDeletionCancel: (token, { signal } = {}) =>
    request('/api/v1/account/deletion/preflight', { method: 'POST', body: { token }, signal }),
  deleteListeningData: () => request('/api/v1/me/listening-data', { method: 'DELETE' }),

  // --- Listening imports and the unrated queue (IMPORT-001 / QUEUE-001) ----
  // The chunk upload is the one raw-bytes request in the app: a chunk is
  // bytes, and wrapping bytes in multipart or JSON would only buy a parser.
  imports: ({ signal } = {}) => request('/api/v1/imports', { signal }),
  importJob: (publicId, { signal } = {}) =>
    request(`/api/v1/imports/${encodeURIComponent(publicId)}`, { signal }),
  createImport: (files) => request('/api/v1/imports', { method: 'POST', body: { files } }),
  finalizeImport: (publicId) =>
    request(`/api/v1/imports/${encodeURIComponent(publicId)}/finalize`, { method: 'POST' }),
  cancelImport: (publicId, { keepalive } = {}) =>
    request(`/api/v1/imports/${encodeURIComponent(publicId)}/cancel`, {
      method: 'POST',
      keepalive,
    }),
  rematchImport: (publicId) =>
    request(`/api/v1/imports/${encodeURIComponent(publicId)}/rematch`, { method: 'POST' }),
  deleteImport: (publicId) =>
    request(`/api/v1/imports/${encodeURIComponent(publicId)}`, { method: 'DELETE' }),
  uploadImportChunk: async (publicId, position, offset, chunk, { signal } = {}) => {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/octet-stream',
      'X-Chunk-Offset': String(offset),
    }
    const token = readCookie(CSRF_COOKIE)
    if (token) headers[CSRF_HEADER] = token
    const response = await fetch(
      `/api/v1/imports/${encodeURIComponent(publicId)}/files/${position}/chunks`,
      { method: 'POST', headers, credentials: 'same-origin', body: chunk, signal },
    )
    const payload = await parseBody(response)
    if (!response.ok) throw new ApiError(payload, response.status)
    return payload
  },
  matchReviews: ({ importId, signal } = {}) =>
    request(
      `/api/v1/match-reviews${importId ? `?import_id=${encodeURIComponent(importId)}` : ''}`,
      { signal },
    ),
  resolveMatchReview: (publicId, recordingId) =>
    request(`/api/v1/match-reviews/${encodeURIComponent(publicId)}/resolve`, {
      method: 'POST',
      body: { recording_id: recordingId },
    }),
  resolveMatchReviewsNone: (ids) =>
    request('/api/v1/match-reviews/resolve-none', { method: 'POST', body: { ids } }),
  queue: ({ offset, signal } = {}) =>
    request(`/api/v1/queue${offset ? `?offset=${offset}` : ''}`, { signal }),
  setQueueState: (targetType, publicId, state) =>
    request(`/api/v1/queue/${targetType}/${encodeURIComponent(publicId)}`, {
      method: 'PUT',
      body: { state },
    }),
  clearQueueState: (targetType, publicId) =>
    request(`/api/v1/queue/${targetType}/${encodeURIComponent(publicId)}`, {
      method: 'DELETE',
    }),
}
